import {
  createDeleteRequest as createHttpDeleteRequest,
  createGetRequest as createHttpGetRequest,
  createPostRequest as createHttpPostRequest,
  createPutRequest as createHttpPutRequest,
  createHeadRequest as createHttpHeadRequest,
} from '@/services/http';
import { flow } from 'fp-ts/lib/function';
import { mapLeft } from 'fp-ts/lib/IOEither';
import { fromIOEither } from 'fp-ts/lib/TaskEither';
import { addAuthorizationHeaders, addBaseHeaders, addFormHeaders, addFormWithJsonHeaders, addJsonHeaders } from '.';
import { ApiError } from '..';
import { normalizePath } from './path';
import { FETCHING_AUTH_HEADER_ERROR } from '../fetch';

type RequestFactory = {
  (url: string): Request;
};

const normalizeUrl = (factory: RequestFactory) => flow(
  normalizePath,
  factory,
);

const baseHeaders = (factory: RequestFactory) => flow(
  factory,
  addBaseHeaders,
);

export const jsonHeaders = (factory: RequestFactory) => flow(
  factory,
  addJsonHeaders,
);

export const formMultipartHeaders = (factory: RequestFactory) => flow(
  factory,
  addFormHeaders,
);

export const formMultipartHeadersWithJson = (factory: RequestFactory) => flow(
  factory,
  addFormWithJsonHeaders,
);

export const authHeaders = (factory: RequestFactory) => flow(
  factory,
  addAuthorizationHeaders,
  mapLeft(error => new ApiError(FETCHING_AUTH_HEADER_ERROR, error.message)),
  fromIOEither,
);

export const createGetRequest = baseHeaders(normalizeUrl(createHttpGetRequest));
export const createHeadRequest = baseHeaders(normalizeUrl(createHttpHeadRequest));
export const createPutRequest = baseHeaders(normalizeUrl(createHttpPutRequest));
export const createPostRequest = baseHeaders(normalizeUrl(createHttpPostRequest));
export const createPostMultipartRequest = baseHeaders(normalizeUrl(createHttpPostRequest));
export const createPutMultipartRequest = baseHeaders(normalizeUrl(createHttpPutRequest));
export const createDeleteRequest = baseHeaders(normalizeUrl(createHttpDeleteRequest));

export const createAuthorizedGetRequest = authHeaders(jsonHeaders(createGetRequest));
export const createAuthorizedHeadRequest = authHeaders(jsonHeaders(createHeadRequest));
export const createAuthorizedPutRequest = authHeaders(jsonHeaders(createPutRequest));
export const createAuthorizedPostRequest = authHeaders(jsonHeaders(createPostRequest));
export const createAuthorizedPostMultipartRequest = authHeaders(formMultipartHeaders(createPostMultipartRequest));
export const createAuthorizedPutMultipartRequest = authHeaders(formMultipartHeaders(createPutMultipartRequest));
export const createAuthorizedDeleteRequest = authHeaders(jsonHeaders(createDeleteRequest));
